// Umsatzsteuer: reine Rechenregeln. Maßgeblich sind die Steuerschlüssel und Sätze des Mandanten in der
// Datenbank (`tax_code`, `tax_rate` mit Gültigkeit); die Tabelle hier ist nur die Vorlage für Grunddaten.
import { mulDiv } from './decimal'
import { addTax, splitGross, sumCents, type Cents, type RateBp, type TaxSplit } from './money'
import type { IsoDate } from './time'

export type TaxCodeKey = 'standard' | 'reduced' | 'zero' | 'exempt'

export interface TaxRatePeriod {
  /** gültig ab (einschließlich) */
  from: IsoDate
  rateBp: RateBp
}

export interface TaxCodeDef {
  code: TaxCodeKey
  label: string
  /** Kategorie nach EN 16931 (BT-151): S = Regelbesteuerung, Z = Nullsatz, E = steuerbefreit */
  category: 'S' | 'Z' | 'E'
  /** aufsteigend nach `from` */
  rates: readonly TaxRatePeriod[]
}

/** Vorlage Deutschland. Die befristete Senkung 2020 ist enthalten, damit Altbelege richtig nachrechnen. */
export const TAX_CODES_DE: readonly TaxCodeDef[] = [
  {
    code: 'standard',
    label: 'Regelsteuersatz',
    category: 'S',
    rates: [
      { from: '2007-01-01', rateBp: 1900 },
      { from: '2020-07-01', rateBp: 1600 },
      { from: '2021-01-01', rateBp: 1900 },
    ],
  },
  {
    code: 'reduced',
    label: 'Ermäßigter Steuersatz',
    category: 'S',
    rates: [
      { from: '2007-01-01', rateBp: 700 },
      { from: '2020-07-01', rateBp: 500 },
      { from: '2021-01-01', rateBp: 700 },
    ],
  },
  { code: 'zero', label: 'Nullsteuersatz', category: 'Z', rates: [{ from: '2007-01-01', rateBp: 0 }] },
  { code: 'exempt', label: 'Steuerfrei', category: 'E', rates: [{ from: '2007-01-01', rateBp: 0 }] },
]

/** Satz eines Steuerschlüssels am Stichtag (Leistungsdatum). Vor dem ersten Eintrag: Fehler. */
export function rateOn(periods: readonly TaxRatePeriod[], date: IsoDate): RateBp {
  let current: TaxRatePeriod | undefined
  for (const period of periods) {
    if (period.from <= date && (!current || period.from >= current.from)) current = period
  }
  if (!current) throw new RangeError(`Für den ${date} ist kein Steuersatz hinterlegt.`)
  return current.rateBp
}

export function defaultRateOn(code: TaxCodeKey, date: IsoDate): RateBp {
  const def = TAX_CODES_DE.find((t) => t.code === code)
  if (!def) throw new RangeError(`Unbekannter Steuerschlüssel: ${code}`)
  return rateOn(def.rates, date)
}

export type PriceMode = 'net' | 'gross'

export interface TaxLine {
  /** Zeilenbetrag in Cent – netto oder brutto, je nach `mode` */
  amount: Cents
  rateBp: RateBp
}

export interface TaxGroup {
  rateBp: RateBp
  net: Cents
  tax: Cents
  gross: Cents
}

export interface TaxTotals {
  /** je Steuersatz, höchster Satz zuerst */
  groups: TaxGroup[]
  net: Cents
  tax: Cents
  gross: Cents
}

/**
 * Belegsummen nach dem Summenverfahren: Zeilen je Steuersatz addieren, die Steuer einmal je Satz aus der
 * Summe rechnen (nicht je Zeile runden). `mode = 'net'` für B2B-Belege, `'gross'` für Kasse und Endkunden.
 */
export function computeTaxTotals(lines: readonly TaxLine[], mode: PriceMode): TaxTotals {
  const sums = new Map<RateBp, Cents>()
  for (const line of lines) sums.set(line.rateBp, (sums.get(line.rateBp) ?? 0) + line.amount)
  const groups: TaxGroup[] = [...sums.entries()]
    .sort(([a], [b]) => b - a)
    .map(([rateBp, amount]) => ({ rateBp, ...(mode === 'net' ? addTax(amount, rateBp) : splitGross(amount, rateBp)) }))
  return {
    groups,
    net: sumCents(groups.map((g) => g.net)),
    tax: sumCents(groups.map((g) => g.tax)),
    gross: sumCents(groups.map((g) => g.gross)),
  }
}

// ---- Belegsummen mit Zeilenanteilen ---------------------------------------------------------------

export interface DocumentTaxLine {
  /** Zeilenbetrag in Cent – netto oder brutto, je nach `mode`; darf negativ sein (Rabatt, Rücknahme) */
  amount: Cents
  rateBp: RateBp
  /** Steuerschlüssel – trennt Gruppen mit gleichem Satz (0 % Nullsatz und 0 % steuerfrei) */
  taxCodeId?: string | null
}

export interface DocumentTaxGroup extends TaxGroup {
  taxCodeId: string | null
}

export interface DocumentTotals {
  /** je Eingabezeile Netto, Steuer, Brutto – je Steuergruppe summieren sie sich exakt zu den Gruppenwerten */
  lines: TaxSplit[]
  /** je Steuerschlüssel und Satz, höchster Satz zuerst (→ document_tax) */
  groups: DocumentTaxGroup[]
  net: Cents
  tax: Cents
  gross: Cents
}

/**
 * Belegsummen: Die Steuer wird EINMAL je Steuergruppe aus der Gruppensumme gerechnet (Summenverfahren) und
 * danach centgenau auf die Zeilen verteilt – jede Zeile bekommt ihre gerundete Steuer, die Rundungsdifferenz
 * zur Gruppe (wenige Cent) tragen die betragsgrößten Zeilen. So gilt je Zeile net + tax = gross, je Gruppe
 * Σ Zeilen = Gruppe und für den Beleg Σ Gruppen = Kopf.
 */
export function documentTotals(lines: readonly DocumentTaxLine[], mode: PriceMode): DocumentTotals {
  const keyOf = (line: DocumentTaxLine): string => `${line.taxCodeId ?? ''}|${line.rateBp}`
  const members = new Map<string, number[]>()
  lines.forEach((line, index) => {
    const list = members.get(keyOf(line))
    if (list) list.push(index)
    else members.set(keyOf(line), [index])
  })
  const result: TaxSplit[] = lines.map(() => ({ net: 0, tax: 0, gross: 0 }))
  const groups: DocumentTaxGroup[] = []
  for (const indexes of members.values()) {
    const first = lines[indexes[0] as number] as DocumentTaxLine
    const rateBp = first.rateBp
    const sum = sumCents(indexes.map((index) => (lines[index] as DocumentTaxLine).amount))
    const group = mode === 'net' ? addTax(sum, rateBp) : splitGross(sum, rateBp)
    const divisor = mode === 'net' ? 10_000 : 10_000 + rateBp
    const taxes = indexes.map((index) => mulDiv((lines[index] as DocumentTaxLine).amount, rateBp, divisor, 'half-up'))
    // Rundungsdifferenz zur Gruppe: je 1 Cent an die betragsgrößten Zeilen (bei Gleichstand die frühere)
    let rest = group.tax - sumCents(taxes)
    const order = indexes
      .map((index, position) => ({ position, weight: Math.abs((lines[index] as DocumentTaxLine).amount) }))
      .filter((entry) => entry.weight > 0)
      .sort((a, b) => b.weight - a.weight || a.position - b.position)
    for (let i = 0; rest !== 0 && order.length > 0; i++) {
      const step = rest > 0 ? 1 : -1
      const target = order[i % order.length] as { position: number }
      taxes[target.position] = (taxes[target.position] as number) + step
      rest -= step
    }
    indexes.forEach((index, position) => {
      const amount = (lines[index] as DocumentTaxLine).amount
      const tax = taxes[position] as number
      result[index] = mode === 'net' ? { net: amount, tax, gross: amount + tax } : { net: amount - tax, tax, gross: amount }
    })
    groups.push({ taxCodeId: first.taxCodeId ?? null, rateBp, ...group })
  }
  groups.sort((a, b) => b.rateBp - a.rateBp || (a.taxCodeId ?? '').localeCompare(b.taxCodeId ?? ''))
  return {
    lines: result,
    groups,
    net: sumCents(groups.map((g) => g.net)),
    tax: sumCents(groups.map((g) => g.tax)),
    gross: sumCents(groups.map((g) => g.gross)),
  }
}
